import { graphql, useFragment } from "react-relay";
import {
  DatasetVersionsHelmetFragment$data,
  DatasetVersionsHelmetFragment$key,
} from "./__generated__/DatasetVersionsHelmetFragment.graphql";
import { LocationContextValue, useLocation } from "@/utils/location";
import { JsonValue, SafeScript } from "./SafeScript";
import { addIfDefined } from "@/utils/helpers";
import { getDatasetVersionPath } from "@/utils/routing";
import { PropsWithChildren } from "react";
import { MetaLayout } from "@/common/MetaLayout";
import { useIntl } from "react-intl";

const Fragment = graphql`
  fragment DatasetVersionsHelmetFragment on Dataset {
    name
    slug
    versionsHelmet: versions {
      nodes {
        id
        version
        publishedAt
      }
    }
  }
`;

interface Props extends PropsWithChildren {
  dataset: DatasetVersionsHelmetFragment$key;
}

export function DatasetVersionsHelmet(props: Props) {
  const dataset = useFragment(Fragment, props.dataset);
  const location = useLocation();
  const intl = useIntl();

  return (
    <MetaLayout
      metaTitle={intl.formatMessage(
        { defaultMessage: "{name} – Versions & Changelog" },
        { name: dataset.name },
      )}
      metaDescription={intl.formatMessage(
        {
          defaultMessage:
            "All releases for {name}. Compare changes, artifacts, and notes across versions.",
        },
        { name: dataset.name },
      )}
    >
      <meta name="robots" content="index, follow" />
      <SafeScript type="application/ld+json">
        {formatItemList(dataset, location)}
      </SafeScript>
      {props.children}
    </MetaLayout>
  );
}

function formatItemList(
  dataset: DatasetVersionsHelmetFragment$data,
  location: LocationContextValue,
): JsonValue {
  const origin = location.origin;

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `${dataset.name} — Versions`,
    ...addIfDefined(
      "itemListElement",
      dataset.versionsHelmet.nodes.length &&
        dataset.versionsHelmet.nodes.map(({ version, publishedAt }, index) => ({
          "@type": "ListItem",
          position: index,
          item: {
            "@type": "Dataset",
            name: dataset.name,
            version: `v.${version}`,
            url: new URL(
              getDatasetVersionPath(dataset.slug, version),
              origin,
            ).toString(),
            ...addIfDefined(
              "datePublished",
              publishedAt && new Date(publishedAt).toISOString(),
            ),
          },
        })),
    ),
  };
}
